import { Slider } from '@miblanchard/react-native-slider';
import React, { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { FilterName } from '@/api/cars/use-car-filters';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { formatNumber, isDesktop, snakeToTitleCase, titleize } from '@/core';
import { BLACK, GREY_10, GREY_30, GREY_60, PINKLING } from '@/style/colors';
import { Button } from '@/ui';

import { CheckItem } from '../check-item';
import { Icon } from '../icon';
import { CaretDown } from '../icons';
import { CaretUp } from '../icons/caret-up';
import { ListItem } from '../list-item';
import { Text } from '../text';

interface ButtonFilterProps {
  name: string;
  headerTitle: string;
  options: any[];
  handlePress: (selectedOptions: number[]) => void;
  onSave: (selectedOptions: number[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onClearAll?: () => void;
  opened?: boolean;
  active?: boolean;
  icon?: React.ReactNode;
  selected?: number[];
  numResults?: number;
  style?: any;
}

export default function ButtonFilter({
  name,
  headerTitle,
  options,
  handlePress,
  onSave,
  onOpen,
  onClose,
  onClearAll,
  opened = false,
  active = false,
  icon,
  selected,
  numResults,
  style,
}: ButtonFilterProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [expanded, setExpanded] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  const [hover, setHover] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<number[]>(
    selected || []
  );
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

  const pressableRef = useRef<View>(null);

  useEffect(() => {
    if (!selected) return;

    setSelectedOptions(selected);
  }, [selected]);

  useEffect(() => {
    setExpanded(opened);
  }, [opened]);

  // useEffect(() => {
  //   if (expanded) {
  //     setSelectedOptions(selected?.length > 0 ? selected : [10, 1000]);
  //   }
  // }, [expanded, selected]);

  const getButtonStyle = () => {
    let stateStyle = styles.default;

    if (active) {
      stateStyle = styles.active;
    }

    if (hover) {
      stateStyle = styles.hover;
    }

    return [styles.button, stateStyle];
  };

  const renderBody = () => {
    if (headerTitle === titleize(FilterName.PRICE)) {
      return (
        <View>
          <Slider
            value={selectedOptions}
            onSlidingComplete={(values) => {
              handlePress(values);
            }}
            onValueChange={(values) => {
              setSelectedOptions(values);
            }}
            minimumValue={10}
            maximumValue={1000}
            step={10}
            minimumTrackTintColor={PINKLING}
            maximumTrackTintColor={
              UnistylesRuntime.themeName === 'dark' ? GREY_60 : GREY_30
            }
            thumbTintColor={
              UnistylesRuntime.themeName === 'dark' ? GREY_10 : GREY_60
            }
          />

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: 4,
            }}
          >
            <View>
              <Text>{options[0].name}</Text>
              <Text>{`$${formatNumber(selectedOptions[0])}`}</Text>
            </View>

            <View style={{ alignItems: 'flex-end' }}>
              <Text>{options[1].name}</Text>
              <Text>{`$${formatNumber(selectedOptions[1])} / day`}</Text>
            </View>
          </View>
        </View>
      );
    } else if (headerTitle === snakeToTitleCase(FilterName.MORE_FILTERS)) {
      return (
        <View>
          <View>
            <Text type="title2">{name}</Text>
          </View>
        </View>
      );
    } else if (headerTitle === snakeToTitleCase(FilterName.SORT_BY)) {
      return (
        <View style={{ gap: 16 }}>
          {options &&
            options.length > 0 &&
            options.map((opt, idx) => {
              return (
                <ListItem
                  key={idx}
                  text={opt.name}
                  onPress={() => {
                    setExpanded(false);

                    setSelectedOptions([idx]);

                    onSave([idx]);
                  }}
                  withBorder={false}
                  style={{ borderBottomWidth: 0 }}
                />
              );
            })}
        </View>
      );
    } else if (headerTitle === snakeToTitleCase(FilterName.MODEL)) {
      const groupedOptions = options.reduce((groups, option, index) => {
        const brand = option.brandName || 'Other';
        if (!groups[brand]) {
          groups[brand] = [];
        }

        groups[brand].push({ ...option, index });

        return groups;
      }, {});

      return (
        <ScrollView
          style={{ maxHeight: 300 }}
          contentContainerStyle={{ gap: 24 }}
        >
          {Object.entries(groupedOptions).map(([brandName, brandOptions]) => (
            <View key={brandName} style={{ gap: 16 }}>
              <Text type="bodyRegular">{brandName}</Text>

              <View style={{ gap: 16, paddingLeft: 8 }}>
                {brandOptions.map((opt: any) => (
                  <CheckItem
                    key={opt.index}
                    text={opt.name}
                    onPress={() => {
                      const isSelected = selectedOptions.includes(opt.index);
                      const updatedSelected = isSelected
                        ? selectedOptions.filter(
                            (item: number) => item !== opt.index
                          )
                        : [...selectedOptions, opt.index];

                      setSelectedOptions(updatedSelected);
                      handlePress(updatedSelected);
                    }}
                    active={selectedOptions?.includes(opt.index)}
                    type={'checkbox'}
                  />
                ))}
              </View>
            </View>
          ))}
        </ScrollView>
      );
    }

    return (
      <ScrollView
        style={{ maxHeight: 300 }}
        contentContainerStyle={{ gap: 16 }}
      >
        {options &&
          options.length > 0 &&
          options.map((opt, idx) => {
            return (
              <CheckItem
                key={idx}
                text={opt.name}
                onPress={() => {
                  const isSelected = selectedOptions.includes(idx);

                  const updatedSelected = isSelected
                    ? selectedOptions.filter((item: number) => item !== idx)
                    : [...selectedOptions, idx];

                  setSelectedOptions(updatedSelected);

                  handlePress(updatedSelected);
                }}
                active={selectedOptions?.includes(idx)}
                type={'checkbox'}
              />
            );
          })}
      </ScrollView>
    );
  };

  const renderApplyButton = () => {
    if (numResults === undefined) {
      return (
        <Button
          text="Apply"
          type="primary"
          onPress={() => {
            setExpanded(false);
            onSave(selectedOptions);
          }}
          style={{ width: 180 }}
        />
      );
    }

    if (numResults === 0) {
      return (
        <Button
          tx="button.noResults"
          type="primary"
          onPress={() => {}}
          style={{ width: 180 }}
          disabled
        />
      );
    }

    return (
      <Button
        tx="button.showResults"
        values={{ number: numResults >= 50 ? '50+' : numResults }}
        type="primary"
        onPress={() => {
          setExpanded(false);
          onSave(selectedOptions);
        }}
        style={{ width: 180 }}
      />
    );
  };

  const handlePressableLayout = () => {
    pressableRef.current?.measure((fx, fy, width, height, px, py) => {
      setModalPosition({ top: py + height, left: px });
    });
  };

  return (
    <View style={style}>
      <Pressable
        ref={pressableRef}
        onLayout={handlePressableLayout}
        onPress={() => {
          if (expanded === false && onOpen) {
            onOpen();
          }

          if (expanded === true && onClose) {
            onClose();
          }

          setExpanded(!expanded);
        }}
        hitSlop={8}
        onPressIn={() => setPressed(true)}
        onPressOut={() => setPressed(false)}
        style={[getButtonStyle(), { opacity: pressed ? 0.5 : 1 }]}
        onHoverIn={() => setHover(true)}
        onHoverOut={() => setHover(false)}
      >
        {icon ? <Icon type="naked" icon={icon} width={25} height={25} /> : null}

        <Text type="bodyRegular">{name}</Text>

        <Icon
          type="naked"
          icon={expanded ? <CaretUp /> : <CaretDown />}
          width={10}
          height={10}
        />
      </Pressable>

      <View>
        <Modal
          animationIn="slideInUp"
          animationInTiming={isDesktop() ? 1 : 300}
          animationOut="slideOutDown"
          animationOutTiming={isDesktop() ? 1 : 300}
          hasBackdrop
          backdropColor={BLACK}
          backdropOpacity={0.5}
          isVisible={expanded}
          style={{
            margin: 0,
            minWidth: 350,

            ...(isDesktop() && {
              position: 'absolute',
              top: modalPosition.top + 10,
              left: modalPosition.left,
            }),
          }}
          onBackdropPress={() => {
            setExpanded(false);
            onClose?.();
          }}
          onDismiss={() => {
            setSelectedOptions(selected || []);
          }}
        >
          <View style={styles.container}>
            <View style={styles.bodyContainer}>
              <View
                style={{
                  width: '100%',
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
              >
                <Text type="title2">{headerTitle}</Text>

                <NavigationButton
                  type={NavigationButtonTypes.cancel}
                  onPress={() => {
                    setExpanded(false);
                    onClose?.();
                  }}
                  style={{ position: 'absolute', right: 0 }}
                />
              </View>

              {renderBody()}
            </View>

            {headerTitle !== snakeToTitleCase(FilterName.SORT_BY) && (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  borderTopWidth: 1,
                  borderTopColor: theme.colors.card.default.borderColor,
                  alignItems: 'center',
                }}
              >
                <View
                  style={{
                    paddingLeft: 24,
                    paddingRight: 24,
                    paddingBottom: 24,
                    paddingTop: 8,
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Button
                    tx="buttonFilter.clearAll"
                    type="ghost"
                    onPress={() => {
                      onClearAll?.();
                    }}
                    disabled={selectedOptions.length === 0}
                    style={{
                      width: 'auto',
                      paddingLeft: 0,
                    }}
                  />

                  {renderApplyButton()}
                </View>
              </View>
            )}
          </View>
        </Modal>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    minHeight: 200,
    marginTop: 'auto',
    backgroundColor: theme.colors.background_2,
    borderTopRightRadius: 16,
    borderTopLeftRadius: 16,
    flexDirection: 'column',
    gap: 16,
    borderRadius: {
      md: 24,
    },
  },
  bodyContainer: {
    padding: 24,
    paddingBottom: {
      xs: 0,
      md: 24,
    },
    gap: 16,
  },
  button: {
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    borderRadius: 8,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
  },
  default: {
    backgroundColor: theme.colors.buttonFilter.default.backgroundColor,
    borderColor: theme.colors.buttonFilter.default.border,
  },
  hover: {
    backgroundColor: theme.colors.buttonFilter.hover.backgroundColor,
    borderColor: theme.colors.buttonFilter.hover.border,
  },
  active: {
    backgroundColor: theme.colors.buttonFilter.active.backgroundColor,
    borderColor: theme.colors.buttonFilter.active.border,
  },
}));
